/* global axios */
import ApiClient from 'dashboard/api/ApiClient';

// GET /cards takes the board filters as query params; array values (stage_id, card_task_type_id,
// priority) serialize as `key[]=…`, which the controller accepts alongside the scalar form.
class CardsAPI extends ApiClient {
  constructor() {
    super('cards', { accountScoped: true });
  }

  get(params) {
    return axios.get(this.url, { params });
  }

  getContactOptions(query = '') {
    return axios.get(`${this.url}/contact_options`, { params: { q: query } });
  }

  // One page of cards, open or closed. Takes the conversation's display id.
  getByConversation(conversationId, page = 1) {
    return axios.get(
      `${this.baseUrl()}/conversations/${conversationId}/cards`,
      {
        params: { page },
      }
    );
  }

  // The only way to change a card's stage or position; it never changes the card's status.
  move(id, { stageId, position }) {
    return axios.post(`${this.url}/${id}/move`, {
      stage_id: stageId,
      position,
    });
  }

  // Writes a card's own attributes and its member lists (e.g. `assignee_ids`), never its stage.
  update(id, card) {
    return axios.patch(`${this.url}/${id}`, { card });
  }

  resolve(id) {
    return axios.post(`${this.url}/${id}/resolve`);
  }

  cancel(id) {
    return axios.post(`${this.url}/${id}/cancel`);
  }

  reopen(id) {
    return axios.post(`${this.url}/${id}/reopen`);
  }

  getActivities(id) {
    return axios.get(`${this.url}/${id}/activities`);
  }

  // Multipart, so the files go with the text in one request; either may be missing.
  createComment(id, { content, files = [] }) {
    const formData = new FormData();
    if (content) formData.append('content', content);
    files.forEach(file => formData.append('attachments[]', file));
    return axios.post(`${this.url}/${id}/activities`, formData);
  }

  // Only the comment's author may edit or remove it; a removed one stays in the history.
  updateComment(id, activityId, content) {
    return axios.patch(`${this.url}/${id}/activities/${activityId}`, {
      content,
    });
  }

  deleteComment(id, activityId) {
    return axios.delete(`${this.url}/${id}/activities/${activityId}`);
  }

  // Every change of the card, oldest first: its activities plus the audited edits.
  getHistory(id) {
    return axios.get(`${this.url}/${id}/activities/history`);
  }

  // A card's tasks. A typed task carries the type's answers in `form_data`; a plain to-do only
  // a title.
  createTask(cardId, task) {
    return axios.post(`${this.url}/${cardId}/tasks`, { task });
  }

  getTask(cardId, taskId) {
    return axios.get(`${this.url}/${cardId}/tasks/${taskId}`);
  }

  updateTask(cardId, taskId, task) {
    return axios.patch(`${this.url}/${cardId}/tasks/${taskId}`, { task });
  }

  // `transition` is one of start, complete, cancel, reopen.
  deleteTask(cardId, taskId) {
    return axios.delete(`${this.url}/${cardId}/tasks/${taskId}`);
  }

  changeTaskStatus(cardId, taskId, transition) {
    return axios.post(`${this.url}/${cardId}/tasks/${taskId}/${transition}`);
  }

  // Schedules a visit request into its visit; answers with the request, carrying the visit.
  scheduleVisit(cardId, taskId, { scheduledAt, assigneeId }) {
    return axios.post(`${this.url}/${cardId}/tasks/${taskId}/schedule_visit`, {
      task: { scheduled_at: scheduledAt, assignee_id: assigneeId },
    });
  }
}

export default new CardsAPI();
